import {
  IDemonstrativeSlot,
  IDemonstrativeSummary,
  IDemonstrativeTransferDirection,
} from "src/@types/store-conciliation";

import {
  DIVERGENT_ICON_CLASSES,
  DTEF_COMPARISON_ROW_ARIA_DIVERGENT,
} from "../../dtef/constants";

export interface DemonstrativeMovementsProps {
  demonstrative: IDemonstrativeSummary | null | undefined;
}

export type GroupDivergence = {
  readonly amount: boolean;
  readonly quantity: boolean;
};

export const NO_DIVERGENCE: GroupDivergence = { amount: false, quantity: false };

export type DemonstrativeGroup = {
  readonly title: string;
  readonly comparable: boolean;
  readonly slots: readonly { slot: IDemonstrativeSlot; label: string }[];
  readonly listsTransfers?: boolean;
};

export const TRANSFER_DIRECTION_LABELS: Readonly<
  Record<IDemonstrativeTransferDirection, string>
> = {
  in: "Entrada",
  out: "Saída",
};

export const TRANSFER_LIST_LABELS = {
  empty: "Nenhuma transferência entre tesourarias no período.",
  direction: "Tipo",
  time: "Hora",
  cashRegister: "Caixa",
  operator: "Operador",
  operation: "Operação",
  amount: "Valor",
} as const;

export const TRANSFER_LIST_TEST_IDS = {
  list: "demonstrative-transfer-list",
  item: "demonstrative-transfer-item",
  empty: "demonstrative-transfer-empty",
} as const;

/**
 * Os rótulos descrevem o movimento do negócio, não o nome da linha no legado.
 * O legado nomeia o lado da tesouraria ao contrário — a linha "SANGRIAS
 * (TESOURARIA)" é a tesouraria suprindo o caixa — e a API já resolve essa
 * inversão nos slots. Aqui só exibimos.
 */
export const DEMONSTRATIVE_GROUPS: readonly DemonstrativeGroup[] = [
  {
    title: "Suprimentos",
    comparable: true,
    slots: [
      {
        slot: "supply_received_in_cash_registers",
        label: "Recebimento nos caixas",
      },
      { slot: "supply_sent_from_treasury", label: "Saída da tesouraria" },
    ],
  },
  {
    title: "Sangrias",
    comparable: true,
    slots: [
      {
        slot: "sangria_sent_from_cash_registers",
        label: "Saída dos caixas para tesouraria",
      },
      {
        slot: "sangria_received_in_treasury",
        label: "Recebimento na tesouraria",
      },
    ],
  },
  {
    title: "Transferência",
    comparable: false,
    listsTransfers: true,
    slots: [
      { slot: "transfer_in", label: "Entrada" },
      { slot: "transfer_out", label: "Saída" },
    ],
  },
  {
    title: "Outros movimentos",
    comparable: false,
    slots: [
      { slot: "merchandise_exchange", label: "Troca de mercadoria" },
      { slot: "financial_products", label: "Produtos financeiros" },
    ],
  },
];

export const DEMONSTRATIVE_LABELS = {
  unavailable:
    "Demonstrativo indisponível para esta conciliação. Reprocesse para carregar.",
  quantityPrefix: "Qtd.",
  ariaDivergent: DTEF_COMPARISON_ROW_ARIA_DIVERGENT,
} as const;

// Mesmo desenho do DtefComparisonRow: rótulo vermelho leva o único ícone, valor vermelho.
export const DEMONSTRATIVE_CLASSES = {
  label: "font-semibold break-words text-sm",
  divergentLabel:
    "font-semibold break-words text-sm text-red-700 inline-flex items-center gap-1",
  amount: "text-sm",
  divergentAmount: "text-sm text-red-700 font-semibold",
  quantity: "text-xs text-slate-500",
  divergentQuantity: "text-xs text-red-700 font-semibold",
  icon: DIVERGENT_ICON_CLASSES,
} as const;

export const DEMONSTRATIVE_TEST_IDS = {
  group: "demonstrative-group",
  row: "demonstrative-row",
  label: "demonstrative-label",
  amount: "demonstrative-amount",
  quantity: "demonstrative-quantity",
} as const;

export const rowTestId = (slot: IDemonstrativeSlot): string =>
  `${DEMONSTRATIVE_TEST_IDS.row}-${slot}`;
